import { useState, ReactNode, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children: ReactNode;
  showOnlyIfTruncated?: boolean;
  maxLength?: number;
}

export const Tooltip = ({ 
  content, 
  children, 
  showOnlyIfTruncated = true, 
  maxLength = 50 
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [tooltipWidth, setTooltipWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  // Show tooltip only if text is longer than maxLength or showOnlyIfTruncated = false
  const shouldShowTooltip = !showOnlyIfTruncated || (content && content.length > maxLength);

  useEffect(() => {
    if (content && isVisible) {
      // Dynamically calculate width based on text length
      const estimatedWidth = Math.min(Math.max(content.length * 4, 350), 650);
      setTooltipWidth(estimatedWidth);
    }
  }, [content, isVisible]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = () => setIsFocused(false);

    // Add event handlers for all inputs and textareas inside
    const inputs = container.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', handleFocusIn);
      input.addEventListener('blur', handleFocusOut);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocusIn);
        input.removeEventListener('blur', handleFocusOut);
      });
    };
  }, []);

  if (!shouldShowTooltip || !content) {
    return <>{children}</>;
  }

  const tooltipStyle = {
    position: 'absolute' as const,
    zIndex: 9999,
    left: '50px',
    top: '90px',
    marginBottom: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#ffffff',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    width: `${tooltipWidth}px`,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    pointerEvents: 'none' as const,
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && !isFocused && (
        <div style={tooltipStyle}>
          {content}
        </div>
      )}
    </div>
  );
}; 